import { ContractBranch } from '../../../../models/Entities/Contracts/ContractBranch';
import { ContractConcept } from '../../../../models/Entities/Contracts/ContractConcept';
import { ContractStage } from '../../../../models/Entities/Contracts/ContractStage';
import { ExcelRow, ExcelSheet, money } from '../../statistics/StatisticsExcel.Functions';
import {
    getSettlementLabel,
    getStagesForBranch,
    getValuationLabel,
    isGroupedInAnyStage,
    isSharedStage,
    resolveAmountCurrency
} from './ConceptGroupedView.Functions';
import {
    allExecutions,
    getEffectiveCoefficient,
    getExecutionTotalFor,
    getQuantityForStage,
    getRelevantExecutionFor,
    getStageScheduleFor,
    getUnitPriceFor,
    isRemoteStage
} from './ConceptGroupedView.Pricing';

/**
 * Exportación de la grilla de conceptos agrupados.
 *
 * Baja lo mismo que muestra la pantalla, con dos diferencias a favor del archivo:
 * trae **todas las ramas** —no sólo la solapa abierta— y va en formato largo, una
 * fila por concepto y visita, que es lo que sirve para hacer una tabla dinámica.
 *
 * Los importes son los de la modalidad que cada etapa liquida, que es la misma
 * regla con la que la pantalla calcula sus totales: si el archivo sumara todas las
 * modalidades, no cerraría contra lo que el usuario ve.
 */

const SI = 'Sí';
const NO = 'No';

/** Las ramas a exportar: las del contrato más el grupo de las compartidas. */
const branchScopes = (
    stages: ContractStage[],
    branches: ContractBranch[]
): { id: number | undefined; name: string }[] => {
    const scopes: { id: number | undefined; name: string }[] = [];

    const sharedCount = (stages || []).filter(isSharedStage).length;
    if (sharedCount > 0) scopes.push({ id: undefined, name: 'Sin rama (todas)' });

    (branches || []).forEach((branch) => {
        scopes.push({ id: branch.id, name: (branch.branchName || '').trim() || `Rama #${branch.id}` });
    });

    // Una etapa puede apuntar a una rama que no está en la lista del addendum.
    // `branchId` llega como number, null o undefined: se normaliza a uno solo.
    const known = new Set<number | undefined>(scopes.map((scope) => scope.id));
    (stages || []).forEach((stage) => {
        const branchId = stage.branchId ?? undefined;
        if (branchId === undefined || known.has(branchId)) return;
        known.add(branchId);
        scopes.push({ id: branchId, name: `Rama #${branchId}` });
    });

    return scopes;
};

const currencyFor = (
    concept: ContractConcept,
    schedule: { currency?: string } | undefined,
    contractCurrency: string | undefined
): string => resolveAmountCurrency(concept.conceptCurrency, schedule?.currency, contractCurrency) || '';

/**
 * Una fila por concepto y visita, en cada rama. Se saltean los conceptos que la
 * pantalla tampoco muestra en esa etapa: sin cuota de pago y sin agrupar en la
 * visita, el concepto no liquida ahí.
 */
export const byStageRows = (
    concepts: ContractConcept[],
    stages: ContractStage[],
    branches: ContractBranch[],
    contractCurrency?: string
): ExcelRow[] => {
    const rows: ExcelRow[] = [];

    branchScopes(stages, branches).forEach((scope) => {
        getStagesForBranch(stages, scope.id).forEach((stage) => {
            (concepts || []).forEach((concept) => {
                const schedule = getStageScheduleFor(concept, stage.id);
                if (!schedule && !isGroupedInAnyStage(concept)) return;

                const execution = getRelevantExecutionFor(concept, schedule, stage);
                const executionLabel =
                    allExecutions.find((item) => item.key === execution)?.label ?? String(execution);

                rows.push({
                    Rama: scope.name,
                    Visita: stage.stageName || `Visita #${stage.id}`,
                    'Aplica a todas las ramas': isSharedStage(stage) ? SI : NO,
                    Orden: stage.stageOrder ?? '',
                    Modalidad: isRemoteStage(stage) ? 'Remota' : 'Presencial',
                    Concepto: concept.concept?.conceptName || `Concepto #${concept.id}`,
                    Liquidación: getSettlementLabel(concept.settlementSetting),
                    Valuación: getValuationLabel(concept.valuationType),
                    'Modalidad que liquida': executionLabel,
                    Moneda: currencyFor(concept, schedule, contractCurrency),
                    'Precio unitario': money(getUnitPriceFor(concept, schedule, execution) ?? 0),
                    Coeficiente: getEffectiveCoefficient(concept, schedule, execution),
                    Cantidad: getQuantityForStage(schedule),
                    Total: money(getExecutionTotalFor(concept, schedule, execution))
                });
            });
        });
    });

    return rows;
};

/** Total por rama y visita, con la moneda separada: nunca se suman monedas distintas. */
export const byStageTotals = (rows: ExcelRow[]): ExcelRow[] => {
    const totals = new Map<string, { rama: string; visita: string; compartida: string; moneda: string; total: number; conceptos: number }>();

    rows.forEach((row) => {
        const moneda = String(row.Moneda ?? '');
        const key = `${row.Rama}::${row.Visita}::${moneda}`;
        const amount = typeof row.Total === 'object' && row.Total !== null ? Number((row.Total as any).value) || 0 : 0;

        const current = totals.get(key) ?? {
            rama: String(row.Rama ?? ''),
            visita: String(row.Visita ?? ''),
            compartida: String(row['Aplica a todas las ramas'] ?? NO),
            moneda,
            total: 0,
            conceptos: 0
        };
        current.total += amount;
        current.conceptos += 1;
        totals.set(key, current);
    });

    return Array.from(totals.values()).map((item) => ({
        Rama: item.rama,
        Visita: item.visita,
        'Aplica a todas las ramas': item.compartida,
        Moneda: item.moneda,
        Conceptos: item.conceptos,
        Total: money(item.total)
    }));
};

/** Las dos tablas que no son por etapa: mismas columnas que en pantalla. */
export const genericRows = (concepts: ContractConcept[], contractCurrency?: string): ExcelRow[] =>
    (concepts || []).map((concept) => ({
        Concepto: concept.concept?.conceptName || `Concepto #${concept.id}`,
        Referencia: (concept.concept?.conceptReferenceId as any) || '',
        Liquidación: getSettlementLabel(concept.settlementSetting),
        Valuación: getValuationLabel(concept.valuationType),
        Moneda: resolveAmountCurrency(concept.conceptCurrency, contractCurrency) || '',
        Precio: money(concept.priceValue ?? 0),
        Presencial: concept.applyToInFaceExecution ? SI : NO,
        Remoto: concept.applyToRemoteExecution ? SI : NO,
        Telefónico: concept.applyToPhoneExecution ? SI : NO
    }));

export interface ConceptGridExport {
    byStage: ContractConcept[];
    inAnyStage: ContractConcept[];
    others: ContractConcept[];
    stages: ContractStage[];
    branches: ContractBranch[];
    currency?: string;
}

/** El libro completo: el detalle por etapa, sus totales, y las otras dos tablas. */
export const conceptGridSheets = (input: ConceptGridExport): ExcelSheet[] => {
    const detail = byStageRows(input.byStage, input.stages, input.branches, input.currency);

    return [
        {
            name: 'Conceptos por etapa',
            description: 'Una fila por concepto y visita, en cada rama. Las visitas sin rama aparecen en todas.',
            note: 'El importe es el de la modalidad que liquida cada etapa, igual que el total de la pantalla.',
            rows: detail
        },
        {
            name: 'Totales por visita',
            description: 'Total liquidado por rama y visita, separado por moneda.',
            rows: byStageTotals(detail)
        },
        {
            name: 'En cualquier etapa',
            description: 'Conceptos que se liquidan en cualquier etapa.',
            rows: genericRows(input.inAnyStage, input.currency)
        },
        {
            name: 'Otros conceptos',
            description: 'Conceptos que no se liquidan por etapa.',
            rows: genericRows(input.others, input.currency)
        }
    ];
};
